import React, { useState, useMemo } from "react";
import {
    Box,
    Typography,
    TextField,
    Slider,
    Grid,
    Paper,
    List,
    ListItem,
    ListItemText,
    Divider,
    Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Footer from "./Footer";

const SipCalculator = () => {
    const theme = useTheme();

    const [monthlyInvestment, setMonthlyInvestment] = useState(25000);
    const [rate, setRate] = useState(12);
    const [years, setYears] = useState(10);
    const [calculatorType, setCalculatorType] = useState("sip");

    // const totalValue = useMemo(() => {
    //     const P = monthlyInvestment;
    //     const r = rate / 100;
    //     const monthlyRate = r / 12;
    //     const n = years * 12;

    //     if (P <= 0 || years <= 0) return 0;

    //     switch (calculatorType) {

    //         case "sip":
    //         case "mf":
    //         case "rd":
    //             if (monthlyRate === 0) return P * n;
    //             return P *
    //                 ((Math.pow(1 + monthlyRate, n) - 1) / monthlyRate) *
    //                 (1 + monthlyRate);

    //         case "lumpsum":
    //             return P * Math.pow(1 + r, years);

    //         case "swp":
    //             let balance = P;
    //             const withdrawal = P * 0.02; // 2% monthly withdrawal
    //             for (let i = 0; i < n; i++) {
    //                 balance = balance * (1 + monthlyRate) - withdrawal;
    //                 if (balance <= 0) return 0;
    //             }
    //             return balance;

    //         case "fd":
    //             return P * Math.pow(1 + r / 4, 4 * years);

    //         case "emi":
    //             if (monthlyRate === 0) return P / n;
    //             return (
    //                 P *
    //                 monthlyRate *
    //                 Math.pow(1 + monthlyRate, n)
    //             ) /
    //                 (Math.pow(1 + monthlyRate, n) - 1);

    //         case "ppf":
    //         case "epf":
    //         case "ssy":
    //             let futureValue = 0;
    //             for (let i = 0; i < years; i++) {
    //                 futureValue = (futureValue + P) * (1 + r);
    //             }
    //             return futureValue;

    //         case "tax":
    //             let income = P;
    //             let tax = 0;

    //             if (income > 1000000) {
    //                 tax += (income - 1000000) * 0.3;
    //                 income = 1000000;
    //             }
    //             if (income > 500000) {
    //                 tax += (income - 500000) * 0.2;
    //                 income = 500000;
    //             }
    //             if (income > 250000) {
    //                 tax += (income - 250000) * 0.05;
    //             }

    //             return tax;

    //         case "xirr":
    //             return P * Math.pow(1 + r, years);

    //         default:
    //             return 0;
    //     }
    // }, [monthlyInvestment, rate, years, calculatorType]);


    const totalValue = useMemo(() => {
        const P = monthlyInvestment;
        const r = rate / 100;
        const nMonths = years * 12;

        if (P <= 0 || years <= 0) return 0;

        switch (calculatorType) {
            case "sip":
            case "mf":
            case "rd": {
                const monthlyRate = Math.pow(1 + r, 1 / 12) - 1; // compound monthly
                return P * ((Math.pow(1 + monthlyRate, nMonths) - 1) / monthlyRate) * (1 + monthlyRate);
            }

            case "lumpsum":
                return P * Math.pow(1 + r, years);

            case "fd": {
                const quarterlyRate = r / 4;
                return P * Math.pow(1 + quarterlyRate, 4 * years);
            }

            case "ppf":
            case "epf":
            case "ssy": {
                let futureValue = 0;
                for (let i = 0; i < years; i++) {
                    futureValue = (futureValue + P) * (1 + r);
                }
                return futureValue;
            }

            case "swp": {
                const monthlyRate = r / 12;
                let balance = P;
                const withdrawal = P * 0.02; // sample withdrawal
                for (let i = 0; i < nMonths; i++) {
                    balance = balance * (1 + monthlyRate) - withdrawal;
                    if (balance <= 0) return 0;
                }
                return balance;
            }

            case "emi": {
                const monthlyRate = r / 12;
                return (monthlyRate === 0) ? P / nMonths :
                    (P * monthlyRate * Math.pow(1 + monthlyRate, nMonths)) / (Math.pow(1 + monthlyRate, nMonths) - 1);
            }

            case "tax": {
                let income = P, tax = 0;
                if (income > 1000000) { tax += (income - 1000000) * 0.3; income = 1000000; }
                if (income > 500000) { tax += (income - 500000) * 0.2; income = 500000; }
                if (income > 250000) { tax += (income - 250000) * 0.05; }
                return tax;
            }

            case "xirr":
                return P * Math.pow(1 + r, years);

            default:
                return 0;
        }
    }, [monthlyInvestment, rate, years, calculatorType]);


    // const investedAmount = useMemo(() => {
    //     const months = years * 12;

    //     switch (calculatorType) {

    //         case "sip":
    //         case "mf":
    //         case "rd":
    //             return monthlyInvestment * months;

    //         case "ppf":
    //         case "epf":
    //         case "ssy":
    //             return monthlyInvestment * years;

    //         case "emi":
    //             return monthlyInvestment; // principal

    //         case "lumpsum":
    //         case "swp":
    //         case "fd":
    //         case "xirr":
    //             return monthlyInvestment;

    //         case "tax":
    //             return monthlyInvestment;

    //         default:
    //             return 0;
    //     }
    // }, [monthlyInvestment, years, calculatorType]);


    const investedAmount = useMemo(() => {
        switch (calculatorType) {
            case "sip":
            case "mf":
            case "rd":
                return monthlyInvestment * years * 12;

            case "ppf":
            case "epf":
            case "ssy":
                return monthlyInvestment * years;

            case "emi":
                return monthlyInvestment; // principal

            case "lumpsum":
            case "fd":
            case "swp":
            case "xirr":
                return monthlyInvestment;

            case "tax":
                return monthlyInvestment;

            default:
                return 0;
        }
    }, [monthlyInvestment, years, calculatorType]);

    const estimatedReturns =
        calculatorType === "emi"
            ? totalValue * years * 12 - investedAmount
            : totalValue - investedAmount;

    const investedPercent =
        totalValue > 0 ? (investedAmount / (calculatorType === "emi"
            ? totalValue * years * 12
            : totalValue)) * 100 : 0;


    const calculatorList = [
        { label: "SIP Calculator", type: "sip" },
        { label: "Lumpsum Calculator", type: "lumpsum" },
        { label: "SWP Calculator", type: "swp" },
        { label: "Mutual Fund Returns Calculator", type: "mf" },
        { label: "Sukanya Samriddhi Yojna Calculator", type: "ssy" },
        { label: "Income Tax Calculator", type: "tax" },
        { label: "PPF Calculator", type: "ppf" },
        { label: "EPF Calculator", type: "epf" },
        { label: "FD Calculator", type: "fd" },
        { label: "RD Calculator", type: "rd" },
        { label: "EMI Calculator", type: "emi" },
        { label: "XIRR Calculator", type: "xirr" },
    ];

    return (
        <Box sx={{
            py: 6, mt: 4,
            // backgroundColor: {
            //     xs: "lightblue",
            //     sm: "red",
            //     md: "lightgreen"
            // }
        }}>
            <Box
                sx={{
                    maxWidth: "1200px",
                    mx: "auto",
                    px: { xs: 2, md: 6 }
                }}
            >
                <Box
                    sx={{
                        pt: { xs: 0, md: 3 },
                        pb: { xs: 3, md: 3 },
                        textAlign: "center"
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: "20px", sm: "28px", md: "40px" },
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            position: "relative",
                        }}
                    >
                        Smart{" "}
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 900,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.text.primary})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                position: "relative",
                                display: "inline-block",
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    bottom: "-4px",
                                    left: 0,
                                    width: "100%",
                                    height: "2px",
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
                                },
                            }}
                        >
                            Financial
                        </Box>{" "}
                        Calculators
                    </Typography>

                </Box>


                <Grid container spacing={4} justifyContent="center" alignItems="stretch">
                    {/* LEFT SECTION */}
                    <Grid item xs={12} md={8}>
                        <Paper
                            sx={{
                                p: { xs: 3, md: 4 },
                                borderRadius: 3,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column"
                            }}
                            elevation={2}
                        >                        <Grid container spacing={4}>
                                {/* INPUT SECTION */}
                                <Grid item xs={12} md={6}>
                                    <Typography mb={1}>Monthly investment</Typography>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={monthlyInvestment}
                                        onChange={(e) =>
                                            setMonthlyInvestment(Number(e.target.value))
                                        }
                                    />
                                    <Slider
                                        value={monthlyInvestment}
                                        min={1000}
                                        max={100000}
                                        step={1000}
                                        onChange={(e, val) => setMonthlyInvestment(val)}
                                        sx={{ mt: 2 }}
                                    />

                                    <Typography mt={1} mb={1}>
                                        Expected return rate (p.a)
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={rate}
                                        onChange={(e) => setRate(Number(e.target.value))}
                                    />
                                    <Slider
                                        value={rate}
                                        min={1}
                                        max={30}
                                        step={0.5}
                                        onChange={(e, val) => setRate(val)}
                                        sx={{ mt: 2 }}
                                    />

                                    <Typography mt={1} mb={1}>
                                        Time period (Years)
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        value={years}
                                        onChange={(e) => setYears(Number(e.target.value))}
                                    />
                                    <Slider
                                        value={years}
                                        min={1}
                                        max={40}
                                        step={1}
                                        onChange={(e, val) => setYears(val)}
                                        sx={{ mt: 2 }}
                                    />
                                </Grid>

                                {/* CHART + SUMMARY */}
                                <Grid item xs={12} md={6}>
                                    <Box display="flex" flexDirection="column">
                                        {/* TOP SECTION */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: 4,
                                            }}
                                        >
                                            {/* DONUT */}
                                            <Box
                                                sx={{
                                                    width: { xs: 180, md: 220 },
                                                    aspectRatio: "1 / 1",
                                                    flexShrink: 0,
                                                    minWidth: { xs: 180, md: 220 },
                                                    borderRadius: "50%",
                                                    background: `conic-gradient(
      ${theme.palette.primary.main} 0% ${investedPercent}%,
      #00C49F ${investedPercent}% 100%
    )`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: { xs: 110, md: 140 },
                                                        height: { xs: 110, md: 140 },
                                                        borderRadius: "50%",
                                                        backgroundColor: "#fff",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <Typography fontSize={14} color="text.secondary">
                                                        Total Value
                                                    </Typography>
                                                    <Typography fontWeight={700} fontSize={16}>
                                                        ₹{Math.round(totalValue).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* SUMMARY */}
                                            <Box
                                                sx={{
                                                    textAlign: { xs: "center", md: "left" },
                                                }}
                                            >
                                                <Typography color="text.secondary">
                                                    Invested amount
                                                </Typography>
                                                <Typography fontWeight={600} mb={2}>
                                                    ₹{investedAmount.toLocaleString()}
                                                </Typography>

                                                <Typography color="text.secondary">
                                                    Est. returns
                                                </Typography>
                                                <Typography fontWeight={600} mb={2}>
                                                    ₹{Math.round(estimatedReturns).toLocaleString()}
                                                </Typography>

                                                <Typography color="text.secondary">
                                                    Total value
                                                </Typography>
                                                <Typography
                                                    fontWeight={700}
                                                    color={theme.palette.primary.main}
                                                >
                                                    ₹{Math.round(totalValue).toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* BUTTON */}
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            size="large"
                                            sx={{
                                                mt: 4,
                                                px: 3,
                                                py: 1,
                                                borderRadius: "8px",
                                                fontWeight: 600,
                                                backgroundColor: "#0080a7",
                                                color: "#fff",
                                                textTransform: "none",
                                                boxShadow: `0 6px 18px ${theme.palette.primary.main}55`,
                                                "&:hover": {
                                                    backgroundColor: theme.palette.primary.dark,
                                                },
                                            }}
                                        >
                                            INVEST NOW
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* RIGHT SIDE */}
                    <Grid item xs={12} md={4}>
                        <Paper
                            sx={{
                                borderRadius: 3,
                                height: "100%",
                                display: "flex",
                                flexDirection: "column"
                            }}
                            elevation={1}
                        >

                            {/* HEADING */}
                            <Typography
                                sx={{
                                    p: 3,
                                    fontWeight: 700,
                                    fontSize: { xs: 16, sm: 18, md: 20 }
                                }}
                            >
                                Popular Calculators
                            </Typography>

                            <Divider />

                            <List>
                                {calculatorList.map((item, index) => (
                                    <React.Fragment key={index}>
                                        <ListItem
                                            button
                                            selected={calculatorType === item.type}
                                            onClick={() => setCalculatorType(item.type)}
                                            sx={{
                                                transition: "0.3s",
                                                "&.Mui-selected": {
                                                    backgroundColor: "#e6f4ea",
                                                },
                                                "&:hover": {
                                                    backgroundColor: "#d4f5dd",
                                                },
                                            }}
                                        >
                                            <ListItemText
                                                primary={item.label}
                                                primaryTypographyProps={{
                                                    fontSize: { xs: 12, sm: 14, md: 16 },
                                                    fontWeight:
                                                        calculatorType === item.type ? 600 : 400,
                                                    color:
                                                        calculatorType === item.type
                                                            ? theme.palette.primary.main
                                                            : theme.palette.text.primary,
                                                }}
                                            />
                                        </ListItem>

                                        {index !== calculatorList.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>

                        </Paper>
                    </Grid>
                </Grid>
                {/* SIP Calculator Information Section */}
                <Box>
                    <Paper sx={{ p: { xs: 3, md: 5 }, mt: 3, borderRadius: 3, mb: 2 }} elevation={1}>

                        <Typography
                            sx={{
                                fontSize: { xs: 18, sm: 22, md: 26 },
                                fontWeight: 700
                            }}
                            gutterBottom
                        >
                            SIP Calculator – Systematic Investment Plan
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: 12, sm: 14, md: 16 }
                            }}
                            color="text.secondary"
                            paragraph
                        >
                            A Systematic Investment Plan (SIP) allows you to invest a fixed
                            amount regularly in mutual funds instead of investing a lump sum.
                            It helps build long-term wealth through disciplined investing and
                            the power of compounding.
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: 16, sm: 18, md: 20 },
                                fontWeight: 600
                            }}
                            gutterBottom
                        >
                            What is a SIP Calculator?
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: 12, sm: 14, md: 16 }
                            }}
                            color="text.secondary"
                            paragraph
                        >
                            A SIP calculator is an online tool that estimates the future value
                            of your monthly investments based on the expected annual return
                            and investment duration. It gives you an approximate maturity
                            amount and total wealth gain.
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: 16, sm: 18, md: 20 },
                                fontWeight: 600
                            }}
                            gutterBottom
                        >
                            How It Works
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: 12, sm: 14, md: 16 }
                            }}
                            color="text.secondary"
                            paragraph
                        >
                            The calculator uses a compound interest formula to determine your
                            maturity amount:
                        </Typography>

                        <Box sx={{ background: "#f5f5f5", p: 2, borderRadius: 2, mb: 2 }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: 12, sm: 14, md: 16 },
                                    fontWeight: 600
                                }}
                            >
                                M = P × ({`{[1 + i]^n – 1} / i`}) × (1 + i)
                            </Typography>
                        </Box>

                        <Typography
                            sx={{
                                fontSize: { xs: 12, sm: 14, md: 16 }
                            }}
                            color="text.secondary"
                            paragraph
                        >
                            Where P is your monthly investment, n is the total number of
                            months invested, and i is the effective monthly return derived
                            from the annual return rate.
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: 16, sm: 18, md: 20 },
                                fontWeight: 600
                            }}
                            gutterBottom
                        >
                            Benefits of Using This Calculator
                        </Typography>

                        <Box
                            component="ul"
                            sx={{
                                pl: 3,
                                fontSize: { xs: 12, sm: 14, md: 16 },
                                color: "text.secondary"
                            }}
                        >
                            <li>Helps you plan long-term financial goals</li>
                            <li>Shows total invested amount</li>
                            <li>Estimates potential wealth gain</li>
                            <li>Saves time compared to manual calculations</li>
                        </Box>

                        <Typography
                            sx={{
                                fontSize: { xs: 12, sm: 14, md: 16 },
                                mt: 2
                            }}
                            color="text.secondary"
                        >
                            Note: The results shown are estimates based on assumed returns.
                            Actual returns may vary depending on market performance and fund
                            expenses.
                        </Typography>

                    </Paper>
                </Box>
            </Box>
            <Footer />


        </Box>
    );
};


export default SipCalculator;